import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { blobProxyPath } from "../modules/product_draft.mjs"
import { purgeUpload } from "../modules/direct_upload_purge.js"
import { MAX_SIZE, UPLOAD_FAILED, validatePhoto } from "../modules/photo_upload.mjs"

// Una foto por direct upload (producto, logo, portada): el hidden lleva el signed_id y el estado
// (empty · uploading · ready · error) se publica en el value y en photo-field:change.
// Doc: _docs/features/images/photo_field.md
export default class extends Controller {
  static targets = ["input", "signedId", "empty", "preview", "image", "progress", "error"]
  static values = {
    uploadUrl: String,
    maxSize: { type: Number, default: MAX_SIZE },
    accept: Array, // content types que el modelo guarda (ImageCompressor::ACCEPTED_CONTENT_TYPES); vacío = cualquier imagen
    state: { type: String, default: "empty" },
    filename: String,
    sessionUpload: Boolean
  }

  connect() {
    this.attempt = 0
    // Tras un 422 la subida de esta sesión sigue siendo purgable y va al borrador
    if (this.sessionUploadValue && this.signedIdTarget.value) {
      this.uploadedSignedId = this.signedIdTarget.value
      this.filename = this.filenameValue
    }
    this.stateValue = this.signedIdTarget.value ? "ready" : "empty"
  }

  // Al borrador solo va una subida de esta sesión
  get draft() {
    return this.uploadedSignedId ? { signedId: this.uploadedSignedId, filename: this.filename || "" } : null
  }

  // Vuelve a mostrar una subida de otra sesión por la ruta proxy; si no carga, solo un blob inexistente vacía el campo
  restoreDraft(photo) {
    if (!photo?.signedId) return { skipped: 0 }
    this.attempt += 1
    this.#markSessionUpload(photo.signedId, photo.filename || "")
    this.signedIdTarget.value = photo.signedId
    const url = blobProxyPath(photo.signedId, this.filename)
    this.imageTarget.addEventListener("error", () => {
      if (this.signedIdTarget.value !== photo.signedId) return
      fetch(url, { method: "HEAD" })
        .then(response => { if (!response?.ok) this.#fail("No pudimos recuperar la foto. Súbela de nuevo.") })
        .catch(() => {})
    }, { once: true })
    this.imageTarget.src = url
    this.stateValue = "ready"
    return { skipped: 0 }
  }

  pick() {
    this.inputTarget.click()
  }

  // Cambiar (o un archivo rechazado) saca la anterior del form y purga la subida de esta sesión
  changed() {
    const file = this.inputTarget.files?.[0]
    if (!file) return
    this.#discardUploaded()
    const problem = this.#validate(file)
    if (problem) return this.#fail(problem)
    this.imageTarget.src = URL.createObjectURL(file)
    this.#upload(file)
  }

  // Quitar vacía el campo y purga un blob recién subido sin guardar
  remove() {
    this.attempt += 1
    this.#discardUploaded()
    this.signedIdTarget.value = ""
    this.inputTarget.value = ""
    this.imageTarget.removeAttribute("src")
    this.stateValue = "empty"
  }

  stateValueChanged() {
    const state = this.stateValue
    const showing = state === "ready" || state === "uploading"
    this.emptyTarget.hidden = showing
    this.previewTarget.hidden = !showing
    this.progressTarget.hidden = state !== "uploading"
    if (state !== "error") this.#showError("")
    this.dispatch("change", { detail: { state } })
  }

  #validate(file) {
    return validatePhoto(file, this.maxSizeValue, this.acceptValue)
  }

  // attempt identifica la subida: una respuesta tardía de un archivo reemplazado no pisa la nueva.
  #upload(file) {
    const attempt = ++this.attempt
    this.stateValue = "uploading"
    this.#progress(0)
    const upload = new DirectUpload(file, this.uploadUrlValue, {
      directUploadWillStoreFileWithXHR: request => {
        request.upload.addEventListener("progress", event => {
          if (event.lengthComputable && attempt === this.attempt) {
            this.#progress(Math.round((event.loaded / event.total) * 100))
          }
        })
      }
    })
    upload.create((error, blob) => {
      // Reemplazada antes de terminar: su blob nunca entró al form, se purga
      if (attempt !== this.attempt) return blob && this.#revert(blob.signed_id)
      if (error) return this.#fail(UPLOAD_FAILED)
      this.#markSessionUpload(blob.signed_id, blob.filename || file.name || "")
      this.signedIdTarget.value = blob.signed_id
      this.stateValue = "ready"
    })
  }

  // Subida de la sesión: queda en los values para que el controller recreado por Turbo la trate igual
  #markSessionUpload(signedId, filename) {
    this.uploadedSignedId = signedId
    this.filename = filename
    this.sessionUploadValue = true
    this.filenameValue = filename
  }

  // Purga la subida de esta sesión que ya no viaja en el form
  #discardUploaded() {
    const uploaded = this.uploadedSignedId
    this.uploadedSignedId = null
    this.filename = ""
    this.sessionUploadValue = false
    this.filenameValue = ""
    if (uploaded) this.#revert(uploaded)
  }

  #fail(message) {
    this.inputTarget.value = ""
    this.imageTarget.removeAttribute("src")
    this.signedIdTarget.value = ""
    this.uploadedSignedId = null
    this.filename = ""
    this.sessionUploadValue = false
    this.filenameValue = ""
    this.stateValue = "error"
    this.#showError(message)
  }

  #progress(percent) {
    this.progressTarget.textContent = `Subiendo… ${percent}%`
  }

  #showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.hidden = !message
  }

  #revert(signedId) {
    purgeUpload(signedId)
  }
}
